import React, { useState } from "react";
import { Input, Button, Table, Tag, Alert, Empty, DatePicker, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLookupMgbMember } from "../hooks/useFetch";
import PromoCard, { money } from "../components/PromoCard";

const card = {
  background: "#fff",
  borderRadius: 10,
  padding: 20,
  marginBottom: 16,
};

// แถวข้อมูล label/value ในการ์ดข้อมูลสมาชิก
const Field = ({ label, value, strong }) => (
  <div style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
    <span style={{ color: "#8c8c8c", minWidth: 150 }}>{label}</span>
    <span style={{ fontWeight: strong ? 700 : 500 }}>{value ?? "-"}</span>
  </div>
);

// โปรวันเกิด — ค้นหาสมาชิกด้วยเบอร์โทร แล้วดูยอดฝากย้อนหลัง 1 เดือน
// นับเฉพาะรายการที่รหัสทำรายการเป็นแบบยาว (ลงท้าย R<เลข>I<เบอร์>) ตามกติกาโปร
const BirthdayPromoPage = () => {
  const [phone, setPhone] = useState("");
  // วันที่ตั้งต้น — ช่วงที่นับคือ 30 วัน โดยวันสุดท้ายคือ "วันก่อนหน้า" วันที่นี้
  const [searchDate, setSearchDate] = useState(dayjs());
  const [result, setResult] = useState(null);

  const { mutateAsync: lookup, isLoading } = useLookupMgbMember();

  const handleSearch = async () => {
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 9) {
      message.error("กรุณากรอกเบอร์โทรให้ครบ");
      return;
    }
    try {
      const { result: data } = await lookup({
        phone: clean,
        date: searchDate.format("YYYY-MM-DD"),
      });
      setResult(data);
      if (data && !data.found) message.warning("ไม่พบสมาชิกที่ใช้เบอร์นี้");
    } catch (error) {
      setResult(null);
      message.error(
        error?.response?.data?.message || "ดึงข้อมูลสมาชิกไม่สำเร็จ"
      );
    }
  };

  const member = result?.member;
  const summary = result?.summary;
  const promo = result?.promo;

  const columns = [
    {
      title: "รหัสทำรายการ",
      dataIndex: "code",
      key: "code",
      render: (v) => <span style={{ fontFamily: "monospace" }}>{v}</span>,
    },
    {
      title: "จำนวน",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 140,
      render: (v) => <span style={{ fontWeight: 600 }}>{money(v)}</span>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "เวลา",
      dataIndex: "time",
      key: "time",
      width: 190,
      sorter: (a, b) => (a.time < b.time ? -1 : 1),
    },
  ];

  return (
    // ⚠️ ต้องกำหนดสีตัวอักษรเอง — App.css ตั้ง body { color: white } ไว้ทั้งแอป
    // ถ้าไม่ใส่ ข้อความจะเป็นสีขาวบนการ์ดขาว = มองไม่เห็น (ข้อมูลมาครบแต่ดูเหมือนว่าง)
    <div style={{ padding: 24, color: "#212121" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#212121" }}>โปรวันเกิด</h1>
        
      </div>

      <div style={card}>
        <div className="flex gap-3 items-center flex-wrap">
          <DatePicker
            value={searchDate}
            onChange={(d) => d && setSearchDate(d)}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Input
            style={{ width: 260 }}
            placeholder="เบอร์โทรลูกค้า เช่น 0953988788"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            maxLength={15}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={isLoading}
            onClick={handleSearch}
          >
            ค้นหา
          </Button>
        </div>
      </div>

      {result && !result.found && (
        <Alert
          type="warning"
          showIcon
          message="ไม่พบสมาชิกที่ใช้เบอร์นี้"
          description="ลองตรวจสอบเบอร์อีกครั้ง หรือค้นหาในระบบ MGB โดยตรง"
        />
      )}

      {promo && member && <PromoCard promo={promo} member={member} />}

      {member && (
        <>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ ...card, flex: "1 1 420px" }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, color: "#212121" }}>ข้อมูลสมาชิก</h3>
              <Field label="ชื่อ-นามสกุล" value={member.fullName} strong />
              <Field label="User ID" value={member.userId} />
              <Field label="วันที่สมัคร" value={member.registeredAt} strong />
              <Field label="เบอร์โทร" value={member.phone} />
              <Field label="Line ID" value={member.lineId} />
              <Field label="ธนาคาร" value={member.bankName} />
              <Field label="เลขบัญชี" value={member.bankAccount} />
              <Field label="รู้จักเราจาก" value={member.knownFrom} />
            </div>

            <div style={{ ...card, flex: "1 1 420px" }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, color: "#212121" }}>ยอดรวมทั้งหมด (จากระบบ MGB)</h3>
              <Field label="เครดิตปัจจุบัน" value={money(member.creditNow)} />
              <Field label="เครดิตเชิญเพื่อน" value={money(member.creditInvite)} />
              <Field label="ยอดเทิร์นที่ต้องทำ" value={money(member.turnoverRequired)} />
              <Field label="ยอดเงินที่ฝากทั้งหมด" value={money(member.totalDeposit)} />
              <Field label="ยอดเงินที่ถอนทั้งหมด" value={money(member.totalWithdraw)} />
              <Field label="กำไร-ขาดทุน" value={money(member.profitLoss)} strong />
              <Field label="โปรที่รับปัจจุบัน" value={member.currentPromo} />
            </div>
          </div>

          <div style={card}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, color: "#212121" }}>
                ยอดฝาก 30 วัน{" "}
                <span style={{ color: "#8c8c8c", fontWeight: 400, fontSize: 14 }}>
                  ({summary?.from?.slice(0, 10)} ถึง {summary?.to?.slice(0, 10)})
                </span>
              </h3>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Tag color="blue">{summary?.count ?? 0} รายการ</Tag>
                <Tag color="green" style={{ fontSize: 16, padding: "4px 12px" }}>
                  รวม {money(summary?.total)} บาท
                </Tag>
              </div>
            </div>

            {summary?.skippedShortCode > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`ไม่นับรายการรหัสสั้นอีก ${summary.skippedShortCode} รายการ (จากทั้งหมด ${summary.totalRowsOnPage} รายการที่ดึงมา)`}
              />
            )}

            <Table
              rowKey={(r) => r.code + r.time}
              dataSource={result.deposits}
              columns={columns}
              bordered
              size="small"
              locale={{ emptyText: <Empty description="ไม่มียอดฝากที่เข้าเกณฑ์ในช่วง 1 เดือน" /> }}
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BirthdayPromoPage;
